import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  type FC,
  type ReactElement,
} from "react";
import SigmaDeviceManager from "@socure-inc/device-risk-sdk";

import Env from "config/environment";
import { useLogger } from "common/logger";
import { useFeatureFlag } from "common/feature_gating";

type SocureDeviceRiskContextType = {
  getSessionToken: () => Promise<string | undefined>;
};

const SocureDeviceRiskContext = createContext<SocureDeviceRiskContextType | undefined>(undefined);

export const SocureDeviceRiskProvider: FC<{ children: ReactElement }> = ({ children }) => {
  const { socureDeviceRiskSDKKey } = Env;
  const logger = useLogger();
  const isInitializedRef = useRef(false);
  const cachedTokenRef = useRef<string | null>(null);
  const pendingPromiseRef = useRef<Promise<string | undefined> | null>(null);
  const socureDeviceRiskEnabled = useFeatureFlag("socure-device-risk", true);

  const fetchToken = useCallback((): Promise<string | undefined> => {
    if (!socureDeviceRiskSDKKey || !socureDeviceRiskEnabled) {
      logger.log("Socure SDK key missing or LD flag not enabled");
      return Promise.resolve(undefined);
    }

    if (cachedTokenRef.current) {
      return Promise.resolve(cachedTokenRef.current);
    }

    if (pendingPromiseRef.current) {
      return pendingPromiseRef.current;
    }

    const promise = SigmaDeviceManager.getSessionToken()
      .then((token) => {
        cachedTokenRef.current = token;
        pendingPromiseRef.current = null;
        return token;
      })
      .catch((error) => {
        pendingPromiseRef.current = null;
        logger.log(error);
        return undefined;
      });

    pendingPromiseRef.current = promise;
    return promise;
  }, [socureDeviceRiskSDKKey, logger]);

  useEffect(() => {
    if (socureDeviceRiskSDKKey && socureDeviceRiskEnabled && !isInitializedRef.current) {
      SigmaDeviceManager.initialize({ sdkKey: socureDeviceRiskSDKKey });
      isInitializedRef.current = true;

      fetchToken().catch((err) => logger.log("Failed to pre-fetch Socure token:", err));
    }
  }, [socureDeviceRiskSDKKey, socureDeviceRiskEnabled, fetchToken, logger]);

  const getSessionToken = useCallback(
    (): Promise<string | undefined> => fetchToken(),
    [fetchToken],
  );

  return (
    <SocureDeviceRiskContext.Provider value={{ getSessionToken }}>
      {children}
    </SocureDeviceRiskContext.Provider>
  );
};

export function useSocureDeviceRisk(): () => Promise<string | undefined> {
  const context = useContext(SocureDeviceRiskContext);

  if (context === undefined) {
    throw new Error("useSocureDeviceRisk must be used within a SocureDeviceRiskProvider");
  }

  return context.getSessionToken;
}
